import { PointerSensor, useSensor } from "@dnd-kit/core";
// eslint-disable-next-line no-restricted-imports
import styled from "@emotion/styled";
import { Tooltip } from "antd";
import cx from "classnames";
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { msgid, ngettext, t } from "ttag";

import Button from "metabase/common/components/Button";
import type {
  DragEndEvent,
  RenderItemProps,
} from "metabase/common/components/Sortable";
import { SortableList } from "metabase/common/components/Sortable";
import CS from "metabase/css/core/index.css";
import { color } from "metabase/lib/colors";
import type { ParametersListProps } from "metabase/parameters/components/ParametersList/types";
import { getVisibleParameters } from "metabase/parameters/utils/ui";
import { Flex, Icon } from "metabase/ui";
import type { Parameter, ParameterId } from "metabase-types/api";

import { ParameterWidget } from "../ParameterWidget";

import { getMeasuredFirstRowItemCount } from "./utils";

const FilterButton = styled(Button)`
  color: ${color("brand")};
  margin: -0.25rem;
`;

const TOGGLE_BUTTON_CLASS_NAME = "js-parameters-list-toggle-button";

const getId = (valuePopulatedParameter: Parameter) =>
  valuePopulatedParameter.id;

export const ParametersList = forwardRef<HTMLDivElement, ParametersListProps>(
  function ParametersList(
    {
      className,

      parameters,
      linkedFilterParameters = parameters,
      cardId,
      dashboardId,
      editingParameter,

      isSortable = true,
      isFullscreen,
      hideParameters,
      isEditing,
      vertical = false,
      commitImmediately = false,

      setParameterValueToDefault,
      setParameterValue,
      setParameterIndex,
      setEditingParameter,
      enableParameterRequiredBehavior,
      widgetsWithinPortal,
      widgetsPopoverPosition,

      hasTestIdProps = true,
    },
    fwdRef,
  ) {
    const pointerSensor = useSensor(PointerSensor, {
      activationConstraint: { distance: 15 },
    });
    const sensors = useMemo(() => [pointerSensor], [pointerSensor]);

    const [persistentFilterIds, setPersistentFilterIds] = useState(() => {
      const visibleParams = getVisibleParameters(parameters, hideParameters);
      const activeParams = visibleParams.filter(
        (p) => p.value != null && p.value !== "",
      );
      return new Set(activeParams.map((p) => p.id));
    });

    const [isExpanded, setIsExpanded] = useState(false);
    const [measuredFirstRowItemCount, setMeasuredFirstRowItemCount] =
      useState(Infinity);
    const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(
      null,
    );

    const allVisibleParameters = useMemo(
      () => getVisibleParameters(parameters, hideParameters),
      [parameters, hideParameters],
    );

    const activeParameters = useMemo(
      () =>
        allVisibleParameters.filter((p) => p.value != null && p.value !== ""),
      [allVisibleParameters],
    );

    const hasActiveParameters = activeParameters.length > 0;

    const handleSetValue = useCallback(
      (parameterId: ParameterId, value: any) => {
        if (setParameterValue) {
          setParameterValue(parameterId, value);
        }
        setPersistentFilterIds((currentIds) => {
          if (currentIds.has(parameterId)) {
            return currentIds;
          }
          const newIds = new Set(currentIds);
          newIds.add(parameterId);
          return newIds;
        });
      },
      [setParameterValue],
    );

    const hasPersistentFilters = persistentFilterIds.size > 0;
    const showFoldedPersistent = !isExpanded && hasPersistentFilters;
    const showFoldedRow = !isExpanded && !hasActiveParameters;

    useLayoutEffect(() => {
      const element = containerNode;

      if (!element || !showFoldedRow) {
        setMeasuredFirstRowItemCount(Infinity);
        return;
      }

      const calculateFirstRowConnt = () => {
        if (!element) {
          return;
        }

        const parameterWidgets = Array.from(element.children).filter(
          (child) =>
            !(child as HTMLElement).classList.contains(
              TOGGLE_BUTTON_CLASS_NAME,
            ),
        ) as HTMLElement[];

        if (parameterWidgets.length === 0) {
          setMeasuredFirstRowItemCount(Infinity);
          return;
        }

        const firstItemTop = parameterWidgets[0].offsetTop;
        let firstRowCount = 0;

        for (const widget of parameterWidgets) {
          if (widget.offsetTop > firstItemTop) {
            break;
          }
          firstRowCount++;
        }

        setMeasuredFirstRowItemCount(
          getMeasuredFirstRowItemCount({
            firstRowCount,
            totalItemCount: parameterWidgets.length,
          }),
        );
      };

      calculateFirstRowConnt();

      const resizeObserver = new ResizeObserver(calculateFirstRowConnt);
      resizeObserver.observe(element);

      return () => resizeObserver.disconnect();
    }, [containerNode, allVisibleParameters.length, showFoldedRow]);

    const isListSortable = isSortable && isExpanded;

    const handleToggleExpand = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    const canFoldToActive =
      hasPersistentFilters &&
      allVisibleParameters.length > persistentFilterIds.size;

    const canFoldToRow =
      !hasPersistentFilters &&
      allVisibleParameters.length > measuredFirstRowItemCount;

    const canEverFoldToRow =
      !hasPersistentFilters && allVisibleParameters.length > 0;

    const showToggleButton =
      canFoldToActive ||
      canFoldToRow ||
      (isExpanded && (canFoldToActive || canEverFoldToRow));

    const handleSortEnd = useCallback(
      ({ id, newIndex }: DragEndEvent) => {
        if (setParameterIndex) {
          setParameterIndex(id as ParameterId, newIndex);
        }
      },
      [setParameterIndex],
    );

    const renderItem = ({
      item: valuePopulatedParameter,
      id,
      index,
    }: RenderItemProps<Parameter>) => {
      if (showFoldedPersistent && !persistentFilterIds.has(id as string)) {
        return null;
      }

      if (
        showFoldedRow &&
        index != null &&
        index >= measuredFirstRowItemCount
      ) {
        return null;
      }

      return (
        <ParameterWidget
          key={`sortable-${id}`}
          fullWidth={vertical}
          withinPortal={widgetsWithinPortal}
          popoverPosition={widgetsPopoverPosition}
          className={cx({ [CS.mb2]: vertical })}
          isEditing={isEditing}
          isFullscreen={isFullscreen}
          parameter={valuePopulatedParameter}
          parameters={linkedFilterParameters}
          cardId={cardId}
          dashboardId={dashboardId}
          editingParameter={editingParameter}
          setEditingParameter={setEditingParameter}
          setValue={
            setParameterValue &&
            ((value) => handleSetValue(valuePopulatedParameter.id, value))
          }
          setParameterValueToDefault={setParameterValueToDefault}
          enableParameterRequiredBehavior={enableParameterRequiredBehavior}
          commitImmediately={commitImmediately}
          dragHandle={
            isListSortable && isEditing && setParameterIndex ? (
              <div
                className={cx(
                  CS.flex,
                  CS.layoutCentered,
                  CS.cursorGrab,
                  "text-inherit",
                )}
              >
                <Icon name="grabber" />
              </div>
            ) : null
          }
          isSortable={isListSortable}
          hasTestId={hasTestIdProps}
        />
      );
    };

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        setContainerNode(node);
        if (typeof fwdRef === "function") {
          fwdRef(node);
        } else if (fwdRef) {
          (fwdRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node;
        }
      },
      [fwdRef],
    );

    const [tooltipVisible, setTooltipVisible] = useState(true);

    useEffect(() => {
      const timer = setTimeout(() => {
        setTooltipVisible(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }, []);

    const handleVisibleChange = (newVisible: boolean) => {
      setTooltipVisible(newVisible);
    };

    return allVisibleParameters.length > 0 ? (
      <Flex
        display="flex"
        direction={vertical ? "column" : "row"}
        align="end"
        wrap="wrap"
        gap="sm"
        className={className}
        ref={setRefs}
        onMouseDown={(e) => {
          if (isEditing) {
            e.stopPropagation();
          }
        }}
      >
        {isSortable ? (
          <SortableList
            items={allVisibleParameters}
            getId={getId}
            renderItem={renderItem}
            onSortEnd={handleSortEnd}
            sensors={sensors}
          />
        ) : (
          <>
            {allVisibleParameters.map((parameter: any, index: any) =>
              renderItem({
                item: parameter,
                id: getId(parameter),
                index,
              }),
            )}
          </>
        )}

        {showToggleButton && (
          <Tooltip
            title={isExpanded ? t`Fold Filters` : t`Expand Filters`}
            open={tooltipVisible}
            onOpenChange={handleVisibleChange}
            className={TOGGLE_BUTTON_CLASS_NAME}
          >
            <FilterButton
              borderless
              primary
              icon={isExpanded ? "chevronup" : "chevrondown"}
              onClick={handleToggleExpand}
            >
              {activeParameters.length > 0
                ? ngettext(
                    msgid`${activeParameters.length} active filter`,
                    `${activeParameters.length} active filters`,
                    activeParameters.length,
                  )
                : t`Filters`}
            </FilterButton>
          </Tooltip>
        )}
      </Flex>
    ) : null;
  },
);
